import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Mic,
  MicOff,
  Shield,
  FileText,
  CheckCircle,
  Lock,
  ChevronDown,
  Check,
  Users,
  Star,
  Zap,
  HelpCircle,
  Loader2,
  Target,
  Clock,
  AlertCircle,
  Search,
  RotateCcw,
  Phone,
  Globe,
} from "lucide-react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Minimal markdown formatter so model output can use *emphasis* and **bold**
function formatMarkdown(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

function parseReportSections(reportContent, t) {
  if (!reportContent || typeof reportContent !== "string") return [];

  const lines = reportContent.split("\n");
  const sections = [];
  let currentTitle = null;
  let currentLines = [];

  for (const line of lines) {
    const trimmed = line.trim();
    const isHeader =
      trimmed.startsWith("**") &&
      trimmed.endsWith("**") &&
      !trimmed.startsWith("***");

    if (isHeader) {
      if (currentTitle) {
        sections.push({
          title: currentTitle,
          content: currentLines.join("\n").trim(),
        });
      }
      currentTitle = trimmed.replace(/\*\*/g, "").trim();
      currentLines = [];
    } else {
      currentLines.push(line);
    }
  }

  if (currentTitle) {
    sections.push({
      title: currentTitle,
      content: currentLines.join("\n").trim(),
    });
  } else if (currentLines.length) {
      sections.push({
        title: t("demo.reflectionTitle"),
        content: currentLines.join("\n").trim(),
      });
  }

  return sections.slice(0, 3);
}

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cx("reveal", isVisible && "reveal-visible", className)}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   VOICE DEMO COMPONENT – LIVE CHECK + TRANSCRIBE
───────────────────────────────────────────────────────────── */
function VoiceDemo({ onStart }) {
  const { t, i18n } = useTranslation();
  const [answer, setAnswer] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isCheckingContext, setIsCheckingContext] = useState(false);
  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [hasEnoughDetail, setHasEnoughDetail] = useState(false);
  const [lastCheckedAnswer, setLastCheckedAnswer] = useState("");

  const textareaRef = useRef(null);
  const [audioStream, setAudioStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [reportSections, setReportSections] = useState([]);
  const [openSectionIndex, setOpenSectionIndex] = useState(0);
  const [generateError, setGenerateError] = useState("");

  const DEMO_QUESTION = t("demo.question");

  // Auto-resize textarea
  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${Math.min(
      textareaRef.current.scrollHeight,
      300
    )}px`;
  }, [answer]);

  // Debounced context check
  useEffect(() => {
    const trimmed = answer?.trim() || "";
    if (!trimmed || trimmed.length < 30) {
      setShowFollowUp(false);
      setFollowUpQuestion("");
      setHasEnoughDetail(false);
      setLastCheckedAnswer("");
      return;
    }

    if (answer === lastCheckedAnswer) return;

    const timeout = setTimeout(() => {
      checkContext(answer);
    }, 1500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, lastCheckedAnswer]);

  const checkContext = async (text) => {
    try {
      setIsCheckingContext(true);
      setLastCheckedAnswer(text);

      const res = await fetch("/api/check-context", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          questionId: 1,
          question: DEMO_QUESTION,
          answer: text,
          previousAnswers: [],
          allQuestions: [{ id: 1, question: DEMO_QUESTION }],
        }),
      });

      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();

      if (data.needsMoreContext && data.followUpQuestion) {
        setFollowUpQuestion(data.followUpQuestion);
        setShowFollowUp(true);
        setHasEnoughDetail(false);
      } else {
        setShowFollowUp(false);
        setFollowUpQuestion("");
        setHasEnoughDetail(true);
      }
    } catch (e) {
      console.error("check-context demo error", e);
      setHasEnoughDetail(true);
    } finally {
      setIsCheckingContext(false);
    }
  };

  const handleChange = (value) => {
    setAnswer(value);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach((t) => t.stop());
        setAudioStream(null);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert(t("demo.errorMicrophone"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result.split(",")[1];
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      const audioData = await base64Promise;

      const response = await fetch("/api/transcribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audioData, mimeType: "audio/webm" }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const data = await response.json();
      if (!data.text) throw new Error("No text returned from transcription");

      setAnswer((prev) => prev + (prev ? " " : "") + data.text);
    } catch (error) {
      console.error("Transcription error:", error);
      alert(error.message || t("demo.errorTranscription"));
    } finally {
      setIsTranscribing(false);
    }
  };

  const wordCount = answer.trim()
    ? answer
        .trim()
        .split(/\s+/)
        .filter(Boolean).length
    : 0;

  const handleGenerateInsights = async () => {
    const trimmed = answer.trim();
    if (!trimmed || trimmed.length < 20 || !hasEnoughDetail) {
      alert(t("demo.errorMoreDetail"));
      return;
    }

    setIsGenerating(true);
    setGenerateError("");
    setReportSections([]);

    try {
      const response = await fetch("/api/generate-category-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: "there",
          categoryName: "Purpose Discovery",
          language: i18n.language,
          responses: [
            {
              question: DEMO_QUESTION,
              answer: trimmed,
            },
          ],
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success || !data.report?.content) {
        throw new Error(data.error || data.details || t("demo.errorFailed"));
      }

      const sections = parseReportSections(data.report.content, t);
      setReportSections(sections);
      setOpenSectionIndex(0);
    } catch (error) {
      console.error("Generate insights error:", error);
      setGenerateError(error.message || t("demo.errorFailed"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTryAgain = () => {
    setReportSections([]);
    setOpenSectionIndex(0);
    setGenerateError("");
    setAnswer("");
    setHasEnoughDetail(false);
    setShowFollowUp(false);
  };

  return (
    <div className="glass rounded-2xl p-4 md:p-6 lg:p-8 subtle-shadow">
      {reportSections.length > 0 ? (
        /* Insights Report View */
        <div className="animate-fadeIn">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <Star className="text-purple-600 fill-purple-600" size={20} />
            <h3 className="text-xl text-gray-900">{t("demo.insightsTitle")}</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">{t("demo.insightsSubtitle")}</p>

          {/* Insights Sections */}
          <div className="space-y-3 mb-6">
            {reportSections.map((section, index) => {
              const isOpen = openSectionIndex === index;
              return (
                <div
                  key={section.title + index}
                  className="glass rounded-xl border border-gray-200/80 overflow-hidden hover-lift"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSectionIndex(isOpen ? -1 : index)
                    }
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-purple-50/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-[11px] font-semibold text-purple-700">
                        {index + 1}
                      </div>
                      <span className="font-semibold text-gray-900">
                        {section.title}
                      </span>
                    </div>
                    <ChevronDown
                      className={cx(
                        "text-gray-400 transition-transform",
                        isOpen && "transform rotate-180"
                      )}
                      size={20}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-2 text-gray-700 leading-relaxed">
                      {section.content
                        .split(/\n{2,}/)
                        .map((para, idx) => (
                          <p
                            key={idx}
                            className="mb-3 last:mb-0"
                            dangerouslySetInnerHTML={{
                              __html: formatMarkdown(para.trim()),
                            }}
                          />
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={onStart}
              className="w-full premium-button text-white font-semibold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 hover-lift"
            >
              <span>{t("demo.getFullProfile")}</span>
              <ArrowRight size={18} />
            </button>
            <button
              type="button"
              onClick={handleTryAgain}
              className="w-full glass border-2 border-gray-200 hover:border-purple-300 text-gray-700 font-medium py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors hover-lift"
            >
              <RotateCcw size={16} />
              <span>{t("demo.tryAgain")}</span>
            </button>
          </div>
        </div>
      ) : (
        /* Input Form View */
        <>
          {/* Header */}
          <div className="flex items-center gap-2 mb-5">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm font-medium text-gray-900">{t("demo.liveDemo")}</span>
          </div>

          {/* Question */}
          <p className="text-lg md:text-xl font-medium text-gray-900 leading-relaxed mb-6">
            {DEMO_QUESTION}
          </p>

          {/* Input */}
          <div className="relative mb-3">
            <textarea
              ref={textareaRef}
              value={answer}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={t("demo.placeholder")}
              className={cx(
                "w-full min-h-[120px] p-4 border-2 rounded-xl resize-none text-gray-800 leading-relaxed placeholder-gray-400",
                isCheckingContext
                  ? "analyzing-border"
                  : hasEnoughDetail
                  ? "border-green-400 bg-green-50/40"
                  : "border-gray-200",
                "focus:border-purple-400 focus:outline-none transition-colors"
              )}
              style={{ fontSize: 16 }}
            />
            <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
              <span>{wordCount} {t("demo.words")}</span>
              {isCheckingContext && <span>{t("demo.analyzing")}</span>}
            </div>
          </div>

          {/* Follow-up */}
          {showFollowUp && followUpQuestion && (
            <div className="mb-4 p-3 rounded-xl bg-purple-50 border border-purple-200 animate-fadeIn">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">
                  <HelpCircle size={16} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-purple-800 mb-1">
                    {t("demo.followUpTitle")}
                  </p>
                  <p className="text-sm text-purple-900">{followUpQuestion}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success when enough detail */}
          {hasEnoughDetail && !showFollowUp && answer.trim().length > 40 && (
            <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 animate-fadeIn">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <p className="text-xs text-green-800">
                  {t("demo.successMessage")}
                </p>
              </div>
            </div>
          )}

          {/* Voice Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isTranscribing}
              className={cx(
                "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all hover-lift",
                isRecording
                  ? "bg-red-500 text-white breathing"
                  : "premium-button text-white animate-pulse",
                isTranscribing && "opacity-50 cursor-not-allowed"
              )}
            >
              {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
              <span>{isRecording ? t("demo.stopRecording") : t("demo.recordAnswer")}</span>
            </button>

            {isTranscribing && (
              <div className="flex items-center gap-2 text-purple-600 animate-fadeIn">
                <Loader2 className="animate-spin" size={16} />
                <span className="text-xs font-medium">{t("demo.transcribing")}</span>
              </div>
            )}
          </div>

          {/* Generate insights CTA */}
          <button
            type="button"
            onClick={handleGenerateInsights}
            disabled={
              !hasEnoughDetail ||
              !answer.trim() ||
              isCheckingContext ||
              isTranscribing ||
              isGenerating
            }
            className={cx(
              "w-full mt-1 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 border transition-all",
              hasEnoughDetail &&
                answer.trim() &&
                !isCheckingContext &&
                !isTranscribing &&
                !isGenerating
                ? "border-purple-300 bg-white/80 text-purple-700 hover-lift"
                : "border-gray-200 bg-white/60 text-gray-400 cursor-not-allowed"
            )}
          >
            {isGenerating && <Loader2 className="animate-spin" size={16} />}
            <span>
              {isGenerating ? t("demo.generating") : t("demo.generateInsights")}
            </span>
            {!isGenerating && <ArrowRight size={16} />}
          </button>

          {generateError && (
            <p className="mt-2 text-[11px] text-red-500 text-center">
              {generateError}
            </p>
          )}

          {/* Visualiser */}
          {isRecording && audioStream && (
            <div className="mb-4 animate-fadeIn">
              <VoiceWaveVisualizer isRecording={isRecording} stream={audioStream} />
            </div>
          )}

          <p className="mt-3 text-xs text-gray-500 text-center">
            {t("demo.footerText")}
          </p>
        </>
      )}
    </div>
  );
}

function VoiceWaveVisualizer({ isRecording, stream }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!isRecording || !stream || typeof window === "undefined") return;

    const AudioCtx =
      window.AudioContext || window.webkitAudioContext || null;
    if (!AudioCtx) return;

    const audioContext = new AudioCtx();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.85;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      if (!isRecording) return;
      animationRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      const { width, height } = canvas;
      ctx.fillStyle = "rgba(249,250,251,0.98)";
      ctx.fillRect(0, 0, width, height);

      const barWidth = (width / bufferLength) * 3.2;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * height * 0.7;
        const gradient = ctx.createLinearGradient(
          0,
          height - barHeight,
          0,
          height
        );
        const hue = (i / bufferLength) * 40 + 260;
        gradient.addColorStop(0, `hsla(${hue},70%,65%,0.9)`);
        gradient.addColorStop(1, `hsla(${hue},75%,55%,1)`);
        ctx.fillStyle = gradient;
        ctx.fillRect(x, height - barHeight, barWidth - 2, barHeight);
        x += barWidth;
      }
    };

    draw();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      try {
        audioContext.close();
      } catch {}
    };
  }, [isRecording, stream]);

  return (
    <div className="rounded-xl overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 p-0.5">
      <canvas
        ref={canvasRef}
        width={600}
        height={64}
        className="w-full h-16 rounded-xl"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────────────────────────── */
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div
      className={cx(
        "border-b border-gray-200 transition-colors",
        isOpen && "bg-gray-50/50"
      )}
    >
      <button
        onClick={onClick}
        className="w-full py-5 px-1 flex items-start justify-between text-left"
      >
        <span className="font-medium text-gray-900 pr-8 leading-relaxed">
          {question}
        </span>
        <ChevronDown
          size={20}
          className={cx(
            "text-gray-400 transition-transform flex-shrink-0 mt-0.5",
            isOpen && "rotate-180 text-purple-600"
          )}
        />
      </button>
      <div
        className={cx(
          "overflow-hidden transition-all duration-300",
          isOpen ? "max-h-96 pb-5" : "max-h-0"
        )}
      >
        <p className="text-base text-gray-600 leading-relaxed px-1">{answer}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN LANDING PAGE
───────────────────────────────────────────────────────────── */
export default function MyPurposeProfileLanding({ onStart }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const faqs = t("faq.items", { returnObjects: true });
  const problemItems = t("problem.quotes", { returnObjects: true });

  const switchLanguage = (lang) => {
    // Update i18n language and persist preference; App-level effect will sync the URL
    i18n.changeLanguage(lang);
    try {
      window.localStorage.setItem("assessment_language", lang);
    } catch (e) {
      // Ignore localStorage errors
    }
    setShowLangMenu(false);
  };

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLangMenu && !event.target.closest('.relative')) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLangMenu]);

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleCheckout = () => {
    window.location.href = "https://buy.stripe.com/aFa6oIcnG2Yx90E3Gabo404";
  };

  return (
    <div className="min-h-screen gradient-bg">
      {/* ─────────────── HEADER ─────────────── */}
      <header className="sticky top-0 z-50 glass border-b border-gray-200/80">
        <div className="max-w-6xl mx-auto px-4 py-2 md:py-4 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2.5"
          >
            <img
              src="/mpp-logo-light.svg"
              alt="MyPurposeProfile"
              className="h-6 md:h-8 w-auto"
            />
          </button>

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100/50 transition-colors"
                aria-label="Switch language"
              >
                <Globe size={16} />
                <span className="uppercase">{i18n.language}</span>
                <ChevronDown size={14} className={cx(showLangMenu && "rotate-180")} />
              </button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-32 glass rounded-xl border border-gray-200/80 shadow-lg overflow-hidden">
                  <button
                    onClick={() => switchLanguage("en")}
                    className={cx(
                      "w-full px-4 py-2.5 text-left text-sm font-medium transition-colors",
                      i18n.language === "en"
                        ? "bg-purple-50 text-purple-700"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    English
                  </button>
                  <button
                    onClick={() => switchLanguage("nl")}
                    className={cx(
                      "w-full px-4 py-2.5 text-left text-sm font-medium transition-colors",
                      i18n.language === "nl"
                        ? "bg-purple-50 text-purple-700"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    Nederlands
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={handleCheckout}
              className="premium-button text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover-lift"
            >
              <span className="hidden sm:inline">{t("header.getProfile")}</span>
              <span className="sm:hidden">{t("header.enroll")}</span>
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* ─────────────── HERO ─────────────── */}
        <section className="px-4 pt-8 pb-4 md:py-8 lg:py-16 xl:py-24">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-3 md:gap-8 lg:gap-12 xl:gap-16 items-start">
              {/* Left: Copy */}
              <div className="animate-fadeIn">
                {/* Trust Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-100 rounded-full text-purple-700 text-sm mb-3 md:mb-6 lg:mb-8">
                  <Users size={14} />
                  <span>{t("hero.trustBadge")}</span>
                </div>

                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-medium text-gray-900 leading-[1.1] mb-2 md:mb-4 lg:mb-6">
                  {t("hero.headline1")}
                  <br />
                  <span className="gradient-text">
                    {t("hero.headline2")}
                  </span>
                </h1>

                {/* Subheadline */}
                <p className="text-base text-gray-600 leading-relaxed mb-3 md:mb-6 lg:mb-8 max-w-lg">
                  {t("hero.subheadline")}
                </p>

                {/* CTA - Desktop */}
                <div className="hidden lg:block">
                  <button
                    onClick={handleCheckout}
                    className="premium-button text-white rounded-xl py-3.5 px-8 font-semibold text-base hover-lift inline-flex items-center gap-3"
                  >
                    <span>{t("hero.cta")}</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              {/* Right: Demo */}
              <Reveal className="animate-slideUp">
                <VoiceDemo onStart={onStart} />
              </Reveal>
            </div>

            {/* Mobile CTA removed – VoiceDemo CTA handles primary action on small screens */}
          </div>
        </section>

        {/* ─────────────── SOCIAL PROOF BAR ─────────────── */}
        <section className="px-4 py-8 md:py-10 border-y border-gray-200/80 bg-white/30">
          <div className="max-w-6xl mx-auto">
            <Reveal className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star size={16} className="text-purple-600" />
                  <span className="text-2xl font-medium text-gray-900">
                    4.9/5
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t("socialProof.satisfactionRating")}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-medium text-gray-900 mb-1">94%</p>
                <p className="text-sm text-gray-600">{t("socialProof.gainedClarity")}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-medium text-gray-900 mb-1">15-20</p>
                <p className="text-sm text-gray-600">{t("socialProof.pageReport")}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Shield size={16} className="text-purple-600" />
                  <span className="text-2xl font-medium text-gray-900">
                    100%
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t("socialProof.moneyBack")}</p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─────────────── PROBLEM + TRANSFORMATION ─────────────── */}
        <section className="px-4 py-12 md:py-16 lg:py-24 animate-slideUp">
          <div className="max-w-6xl mx-auto">
            {/* Problem / Pain - interactive quotes */}
            <div className="mb-10 md:mb-14">
              <div className="grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)] gap-8 md:gap-10 items-start">
                {/* Left column: heading + copy */}
                <div className="order-1 text-left">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-900 mb-3 md:mb-4">
                    {t("problem.heading")}
                  </h2>
                  <p className="text-base text-gray-600">
                    {t("problem.description")}
                  </p>
                </div>

                {/* Right column: static quote cards */}
                <Reveal className="order-2 md:order-2">
                  <div className="space-y-4">
                    {problemItems.map((item, index) => (
                      <div
                        key={index}
                        className="glass rounded-2xl px-4 py-4 md:px-5 md:py-5 hover-lift"
                      >
                        <blockquote className="text-sm md:text-base font-medium text-gray-900">
                          "{item.quote}"
                        </blockquote>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-gray-600">
                    {t("problem.conclusion")}
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="divider max-w-3xl mx-auto my-8 md:my-12" />

            {/* Transformation */}
            <div>
              <Reveal className="grid lg:grid-cols-2 gap-8 md:gap-10 items-center">
                {/* Visual: product mock-up - left on desktop, below text on mobile */}
                <div className="flex justify-center lg:justify-start order-2 lg:order-1">
                  <div className="relative w-full max-w-sm md:max-w-md">
                    <img
                      src="/mock-up.png"
                      alt="MyPurposeProfile product preview"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>

                {/* Outcomes / list - right on desktop, above image on mobile */}
                <div className="order-1 lg:order-2">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-900 mb-3 md:mb-4">
                    {t("transformation.heading")}
                  </h2>
                  <p className="text-base text-gray-600 mb-6 md:mb-8">
                    {t("transformation.description")}
                  </p>

                  <div className="space-y-5 md:space-y-6">
                    {t("transformation.outcomes", { returnObjects: true }).map((item, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={14} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm md:text-base font-medium text-gray-900 mb-1">
                            {item.title}
                          </p>
                          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 text-left lg:text-left">
                    <button
                      onClick={handleCheckout}
                      className="premium-button text-white rounded-xl py-3.5 px-8 font-semibold hover-lift inline-flex items-center gap-2"
                    >
                      <span>{t("transformation.cta")}</span>
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─────────────── HOW IT WORKS ─────────────── */}
        <section className="px-4 py-12 md:py-16 lg:py-24 bg-white/30">
          <div className="max-w-6xl mx-auto">
            {/* Headline + subheadline */}
            <div className="mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-900 mb-3">
                {t("howItWorks.heading")}
              </h2>
              <p className="text-base text-gray-600 max-w-2xl">
                {t("howItWorks.subheadline")}
              </p>
            </div>

            {/* 3x2 grid - minimal styling */}
            <Reveal className="grid md:grid-cols-3 gap-6 md:gap-8">
              {t("howItWorks.features", { returnObjects: true }).map((item, index) => {
                const icons = [Mic, Zap, Target, FileText, CheckCircle, Clock];
                const IconComponent = icons[index];
                return (
                <div
                  key={item.title}
                  className="flex flex-col gap-3 p-4 md:p-5 rounded-2xl hover-lift bg-white/70"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-base font-medium text-gray-900">
                    {item.title}
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                );
              })}
            </Reveal>
          </div>
        </section>

        {/* ─────────────── TESTIMONIALS ─────────────── */}
        <section className="px-4 py-12 md:py-16 lg:py-24">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-900 mb-3">
                {t("testimonials.heading")}
              </h2>
              <p className="text-base text-gray-600">
                {t("testimonials.subheadline")}
              </p>
            </div>

            <Reveal className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {t("testimonials.items", { returnObjects: true }).map((item, i) => {
                const images = ["/sophie-anne.jpg", "/tony.jpg", "/luuk.png"];
                return (
                <div
                  key={item.name}
                  className="border border-gray-200 rounded-2xl p-6 flex flex-col h-full bg-white/80 hover-lift"
                >
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center gap-1 text-amber-400 text-sm mb-3">
                      <span>★★★★★</span>
                    </div>
                    <h3 className="text-lg md:text-xl font-medium text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {item.body}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-gray-200">
                    <div className="flex items-center gap-4">
                      <img
                        src={images[i]}
                        alt={item.name}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover flex-shrink-0"
                      />
                      <div>
                        <p className="text-base font-medium text-gray-900">
                          {item.name}
                        </p>
                        {item.role && (
                          <p className="text-sm text-gray-600">{item.role}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </Reveal>
          </div>
        </section>

        <div className="divider max-w-6xl mx-auto my-8 md:my-10" />

        {/* ─────────────── WHAT YOU GET + PRICING ─────────────── */}
        <section className="px-4 py-12 md:py-16 lg:py-24 bg-white/30">
          <div className="max-w-2xl mx-auto">
            <Reveal className="glass rounded-2xl p-8 md:p-10 subtle-shadow">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-3">
                  {t("offer.heading")}
                </h2>
                <p className="text-base text-gray-600 mb-6">
                  {t("offer.description")}
                </p>
                <div className="text-4xl md:text-5xl font-medium text-gray-900">{t("offer.price")}</div>
              </div>

              <div className="space-y-4 mb-6">
                {t("offer.features", { returnObjects: true }).map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-base  text-gray-900 mb-0.5">
                        {item.title}
                      </p>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bonus */}
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                <div className="flex items-start gap-3">
                  <Phone className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {t("offer.bonus.title")}
                    </p>
                    <p className="text-xs text-green-700">
                      {t("offer.bonus.desc")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Money-back guarantee */}
              <div className="mb-8 flex items-center justify-center gap-2 text-sm text-gray-600">
                <Shield size={16} className="text-purple-600" />
                <span>{t("offer.guarantee")}</span>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full premium-button text-white rounded-xl py-3.5 font-semibold text-base hover-lift flex items-center justify-center gap-2 mb-4"
              >
                <span>{t("offer.cta")}</span>
                <ArrowRight size={18} />
              </button>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <Lock size={12} />
                <span>{t("offer.payment")}</span>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─────────────── FAQ ─────────────── */}
        <section className="px-4 py-12 md:py-16 lg:py-24">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-900 mb-8 md:mb-10 text-center">
              {t("faq.heading")}
            </h2>

            <div className="border-t border-gray-200">
              {faqs.map((faq, i) => (
                <FAQItem
                  key={i}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === i}
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ─────────────── FINAL CTA ─────────────── */}
        <section className="px-4 py-12 md:py-16 lg:py-24 bg-gradient-to-br from-purple-50 via-purple-50/50 to-pink-50/30">
          <div className="max-w-6xl mx-auto text-center">
            <Reveal>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 mb-4 md:mb-5">
                {t("finalCta.heading")}
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-8 md:mb-10">
                {t("finalCta.subheadline")}
              </p>

              <button
                onClick={handleCheckout}
                className="premium-button text-white rounded-xl py-4 md:py-5 px-10 md:px-12 font-semibold text-lg md:text-xl hover-lift inline-flex items-center gap-3 shadow-lg"
              >
                <span>{t("finalCta.cta")}</span>
                <ArrowRight size={20} />
              </button>
            </Reveal>
          </div>
        </section>
      </main>

      {/* ─────────────── FOOTER ─────────────── */}
      <footer className="px-4 py-8 border-t border-gray-200/80">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/mpp-logo-light.svg"
              alt="MyPurposeProfile"
              className="h-7 w-auto"
            />
            <span className="text-sm text-gray-600">
              {t("footer.copyright")}
            </span>
          </div>

          <nav className="flex items-center gap-6 text-sm text-gray-600">
            <a
              href={`/${i18n.language || "en"}/legal/terms-of-use`}
              className="hover:text-gray-900 transition-colors"
            >
              {t("footer.terms")}
            </a>
            <a
              href={`/${i18n.language || "en"}/legal/privacy-policy`}
              className="hover:text-gray-900 transition-colors"
            >
              {t("footer.privacy")}
            </a>
            <a
              href={`/${i18n.language || "en"}/legal/cookie-policy`}
              className="hover:text-gray-900 transition-colors"
            >
              {t("footer.cookie")}
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
